import React, { useState, useEffect } from 'react'
import {
    Box, Card, CardContent, Grid, LinearProgress, Typography, CircularProgress
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import ProviderAPI from 'config/api/ProviderAPI'
import {
    checkCompanyProfileCompletion,
    checkRecommendationCardCompletion,
    checkAboutCompanyCompletion,
    checkCountiesCompletion,
    checkTechnologyCompletion,
    checkProjectsCompletion,
} from 'config/helpers/Helpers'

const PROFILE_SECTIONS = [
    { label: 'Public Profile',      anchor: 'section-company-details',  checker: checkCompanyProfileCompletion },
    { label: 'Customer Summary',    anchor: 'section-recommendation',   checker: checkRecommendationCardCompletion },
    { label: 'Company Bio',         anchor: 'section-about-company',    checker: checkAboutCompanyCompletion },
    { label: 'Coverage',            anchor: 'section-counties',         checker: checkCountiesCompletion },
    { label: 'Technology',          anchor: 'section-technology',       checker: checkTechnologyCompletion },
    { label: 'Case Studies',        anchor: 'section-case-studies',     checker: checkProjectsCompletion },
]

function scrollToAnchor(anchor) {
    const el = document.getElementById(anchor)
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
}

function CheckItem({ done, label, loading, onClick }) {
    return (
        <Box
            display='flex'
            alignItems='center'
            style={{ cursor: onClick ? 'pointer' : 'default', padding: '2px 0' }}
            onClick={onClick}
        >
            {loading
                ? <CircularProgress size={18} style={{ marginRight: 8, color: '#aaa' }} />
                : done
                    ? <CheckCircleIcon style={{ color: '#229626', marginRight: 8, fontSize: 20 }} />
                    : <RadioButtonUncheckedIcon style={{ color: '#ccc', marginRight: 8, fontSize: 20 }} />
            }
            <Typography
                variant='body2'
                style={{
                    fontWeight: done ? 600 : 400,
                    color: onClick ? '#3085d6' : (done ? '#229626' : '#555'),
                    textDecoration: onClick ? 'underline' : 'none',
                }}
            >
                {label}
            </Typography>
        </Box>
    )
}

function ProviderProgressCards({ company }) {
    const api = new ProviderAPI()
    // null = not yet loaded, false = no card, true = card present
    const [hasPaymentMethod, setHasPaymentMethod] = useState(null)

    useEffect(() => {
        fetchPaymentMethod()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company.companyId])

    async function fetchPaymentMethod() {
        if (!company.stripeCustomerId) {
            setHasPaymentMethod(false)
            return
        }
        try {
            const method = await api.getPaymentMethod(company.companyId)
            setHasPaymentMethod(!!(method && (method.id || method.brand)))
        } catch {
            setHasPaymentMethod(false)
        }
    }

    // ── Profile Completion ──────────────────────────────────────────────────
    const completedCount = PROFILE_SECTIONS.filter(s => s.checker(company) === 100).length
    const totalCount = PROFILE_SECTIONS.length
    const progressPercent = Math.round((completedCount / totalCount) * 100)

    // ── Onboarding Checks ───────────────────────────────────────────────────
    const hasPrimaryPerson = !!(
        company?.primaryPerson?.firstName?.trim() &&
        company?.primaryPerson?.lastName?.trim() &&
        company?.primaryPerson?.email?.trim() &&
        company?.primaryPerson?.phone?.trim()
    )
    const hasDeliveryMethod = !!(company?.deliveryDetails?.deliveryMethod)

    return (
        <Box mb={1}>
            {/* ── Profile Completion Card ─────────────────────────────────── */}
            <Card style={{ marginBottom: '0.5rem' }}>
                <CardContent style={{ padding: '10px 16px', paddingBottom: '10px !important' }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' mb={0.5}>
                        <Typography variant='h5' style={{ margin: 0, fontWeight: 700 }}>
                            Profile Completion
                        </Typography>
                        <Typography
                            variant='body2'
                            style={{ margin: 0, fontWeight: 600, color: progressPercent === 100 ? '#229626' : '#555' }}
                        >
                            {completedCount} / {totalCount} &middot; {progressPercent}%
                        </Typography>
                    </Box>

                    <Box mb={1} position='relative'>
                        <LinearProgress
                            variant='determinate'
                            value={progressPercent}
                            style={{ height: '5px', borderRadius: '3px', backgroundColor: '#e0e0e0' }}
                        />
                        <Box
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: `${progressPercent}%`,
                                height: '5px',
                                borderRadius: '3px',
                                backgroundColor: '#229626',
                                transition: 'width 0.4s ease',
                            }}
                        />
                    </Box>

                    <Grid container spacing={0}>
                        {PROFILE_SECTIONS.map(section => {
                            const done = section.checker(company) === 100
                            return (
                                <Grid item xs={12} sm={6} md={4} key={section.anchor}>
                                    <CheckItem
                                        done={done}
                                        label={section.label}
                                        onClick={() => scrollToAnchor(section.anchor)}
                                    />
                                </Grid>
                            )
                        })}
                    </Grid>
                </CardContent>
            </Card>

            {/* ── Onboarding Checklist Card ───────────────────────────────── */}
            <Card>
                <CardContent style={{ padding: '10px 16px', paddingBottom: '10px !important' }}>
                    <Typography variant='h5' style={{ margin: '0 0 6px', fontWeight: 700 }}>
                        Onboarding Checklist
                    </Typography>
                    <Grid container spacing={0}>
                        <Grid item xs={12} sm={4}>
                            <CheckItem
                                done={hasPrimaryPerson}
                                label='Admin Added'
                                onClick={() => scrollToAnchor('section-primary-person')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CheckItem
                                done={hasPaymentMethod === true}
                                loading={hasPaymentMethod === null}
                                label='Card Added'
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CheckItem
                                done={hasDeliveryMethod}
                                label='Delivery Method Selected'
                                onClick={() => scrollToAnchor('section-delivery-settings')}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    )
}

export default ProviderProgressCards
